import { Button, Container } from "@material-ui/core";
import { WhatsApp } from "@material-ui/icons";
import useLanguageStore from "@stores/languageStore";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/SEOHead";
import SectionHeader from "../../components/SectionHeader";
import afterRefurbishment1 from "../../static/after_refurbishment_1.jpeg";
import afterRefurbishment2 from "../../static/after_refurbishment_2.jpeg";
import beforeRefurbishment1 from "../../static/before_refurbishment_1.jpeg";

const RefurbishmentPage = () => {
	const { t } = useTranslation();
	const { language } = useLanguageStore();
	const isRtl = language === "he";

	// Detect mobile device for WhatsApp link
	const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
	const whatsappLink = isMobile
		? "whatsapp://send?phone=972505774257"
		: "https://wa.me/972505774257";

	return (
		<div
			className={`min-h-[calc(100vh-64px)] py-20 sm:py-32 max-w-[100vw] overflow-x-hidden ${
				isRtl ? "rtl" : "ltr"
			}`}
		>
			<SEOHead
				titleKey="refurbishmentPage.meta.title"
				descriptionKey="refurbishmentPage.meta.description"
			/>
			<Container maxWidth="lg" className="px-4 sm:px-8">
				<SectionHeader
					title={t("refurbishmentPage.title")}
					className="mb-12"
					titleWeight="800"
				/>

				<div
					className={`leading-relaxed text-base mb-8 max-w-5xl mx-auto ${isRtl ? "text-right" : "text-left"}`}
				>
					{/* Intro */}
					<section className="mb-12 text-lg">
						<p className="mb-2">{t("refurbishmentPage.intro.text1")}</p>
						<p className="mb-2 font-bold text-[#063447]">
							{t("refurbishmentPage.intro.text2")}
						</p>
						<p>{t("refurbishmentPage.intro.text3")}</p>
					</section>

					{/* Before and After Images - Subtly and professionally */}
					<section className="mb-16">
						<h2 className="text-2xl font-bold mb-8 text-[#063447] text-center">
							{t("refurbishmentPage.beforeAfter.title")}
						</h2>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
							{/* Comparison 1 */}
							<div className="space-y-4">
								<div className="relative rounded-lg overflow-hidden shadow-xl h-80 group">
									<img
										src={beforeRefurbishment1}
										alt={t("refurbishmentPage.images.beforeAlt")}
										className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
									/>
									<div className="absolute top-4 right-4 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-bold">
										{t("refurbishmentPage.beforeAfter.before")}
									</div>
								</div>
							</div>
							<div className="space-y-4">
								<div className="relative rounded-lg overflow-hidden shadow-xl h-80 group">
									<img
										src={afterRefurbishment1}
										alt={t("refurbishmentPage.images.afterAlt")}
										className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
									/>
									<div className="absolute top-4 left-4 bg-[#063447]/80 text-white px-3 py-1 rounded-full text-sm font-bold">
										{t("refurbishmentPage.beforeAfter.after")}
									</div>
								</div>
							</div>
						</div>
						{/* Additional After Image showcasing detail */}
						<div className="mt-8 flex justify-center">
							<div className="relative rounded-lg overflow-hidden shadow-xl max-w-2xl h-80 w-full group">
								<img
									src={afterRefurbishment2}
									alt={t("refurbishmentPage.images.detailAlt")}
									className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
								/>
								<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-white text-center">
									<span className="font-semibold text-lg">
										{t("refurbishmentPage.intro.text3")}
									</span>
								</div>
							</div>
						</div>
					</section>

					{/* Steps */}
					<section className="mb-12">
						<h2 className="text-2xl font-bold mb-6 text-[#063447]">
							{t("refurbishmentPage.steps.title")}
						</h2>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{[1, 2, 3, 4].map((num) => (
								<div
									key={num}
									className="bg-white p-6 rounded-xl shadow-sm border border-stone-100 hover:shadow-md transition-shadow"
								>
									<h3 className="font-bold text-xl text-[#063447] mb-2">
										{t(`refurbishmentPage.steps.step${num}.title`)}
									</h3>
									<p className="text-gray-700">
										{t(`refurbishmentPage.steps.step${num}.text`)}
									</p>
								</div>
							))}
						</div>
					</section>

					{/* Warning */}
					<section className="mb-12">
						<div className="bg-amber-50 p-8 rounded-xl border-r-4 border-amber-500">
							<h2 className="text-2xl font-bold mb-4 text-amber-800">
								{t("refurbishmentPage.warning.title")}
							</h2>
							<p className="text-amber-900 leading-relaxed">
								{t("refurbishmentPage.warning.text")}
							</p>
						</div>
					</section>

					{/* CTA */}
					<section className="bg-[#063447] text-white p-10 rounded-2xl text-center shadow-2xl">
						<h2 className="text-3xl font-bold mb-4">
							{t("refurbishmentPage.cta.title")}
						</h2>
						<p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
							{t("refurbishmentPage.cta.text")}
						</p>
						<Button
							variant="contained"
							color="default"
							size="large"
							className="!bg-[#25D366] !text-white !font-bold !py-3 !px-8 !rounded-full hover:!bg-[#20bd5a] shadow-lg transform transition-transform hover:scale-105"
							href={whatsappLink}
							target={isMobile ? "_self" : "_blank"}
							startIcon={<WhatsApp />}
						>
							{t("refurbishmentPage.cta.button")}
						</Button>
					</section>
				</div>
			</Container>
		</div>
	);
};

export default RefurbishmentPage;


